import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUserCheck,
  faCheck,
  faTimes,
  faStore,
  faEnvelope,
  faPhone,
  faClock,
  faDesktop,
  faUndo,
  faSearch,
  faSave
} from '@fortawesome/free-solid-svg-icons';
import { useDashboardAuth } from '../../context/DashboardAuthContext';
import api from '../../services/api';
import './Registrations.css';

const MAX_DEVICES = 50;

const isoDate = (date) => date.toISOString().slice(0, 10);

// A subscription runs a year by default: the usual length of a Lonnii subscription.
const defaultDates = () => {
  const start = new Date();
  const end = new Date();
  end.setFullYear(end.getFullYear() + 1);
  return { startDate: isoDate(start), endDate: isoDate(end) };
};

// An approved shop has a running subscription when its latest active one has not ended.
const hasRunningSubscription = (shop) =>
  Boolean(shop.subscription_end) && new Date(shop.subscription_end) >= new Date(new Date().toDateString());

// A shop registered from the phone app waits already online: it has no computer, so it can only run online.
const isPhoneShop = (shop) => shop.phone_only === true || (shop.mode === 'online' && shop.approval_status !== 'approved');

// Where a card's controls start. Pending shops start local with three machines (one-click approval), or online for
// a phone shop; an already-approved shop starts from what it is now.
const initialSettings = (shop, managing) => ({
  mode: managing || isPhoneShop(shop) ? shop.mode || 'local' : 'local',
  maxDevices: managing ? shop.max_devices || 3 : 3,
  montant: '',
  newSubscription: false,
  ...defaultDates()
});

// Desktop shops. "En attente" and "Refusées" handle new registrations (the owner's email is already confirmed;
// the shop cannot activate until approved). "Gérer" finds a shop that is already approved and changes how it runs.
const Registrations = () => {
  const { admin, hasPermission } = useDashboardAuth();
  const [tab, setTab] = useState('pending'); // 'pending' | 'rejected' | 'manage'
  const [shops, setShops] = useState([]);
  const [settings, setSettings] = useState({}); // per shop id
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [notification, setNotification] = useState(null);
  const [loadError, setLoadError] = useState(null);

  const managing = tab === 'manage';
  const canManage = admin?.role === 'owner' || admin?.role === 'super_admin' || hasPermission('approvals.manage');

  const notify = (type, message) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 5000);
  };

  const describeError = (error) => {
    // An empty list and a failed request must not look the same: "nothing waiting" is good news,
    // "the server does not know this page" (404, backend not deployed yet) is not.
    const status = error.response?.status;
    if (status === 404) return 'Le serveur ne connaît pas encore cette page (404). Le backend doit être déployé et rechargé.';
    if (status === 403) return "Vous n'avez pas la permission de voir cette page.";
    return error.response?.data?.error || (status ? `Erreur du serveur (${status})` : 'Serveur injoignable');
  };

  const fetchShops = useCallback(async (quiet = false) => {
    // "Gérer" starts empty: there are far too many approved shops to list, so it waits for a search.
    if (tab === 'manage') {
      const term = search.trim();
      if (term.length < 2) {
        setShops([]);
        setLoadError(null);
        setLoading(false);
        return;
      }
    }

    if (!quiet) setLoading(true);
    try {
      const url = tab === 'manage'
        ? `/dashboard-admin/registrations/shops?search=${encodeURIComponent(search.trim())}`
        : `/dashboard-admin/registrations?status=${tab}`;
      const response = await api.get(url);
      setShops(response.data || []);
      setLoadError(null);
    } catch (error) {
      console.error('Error fetching registrations:', error);
      setLoadError(describeError(error));
      if (!quiet) setShops([]);
    } finally {
      setLoading(false);
    }
  }, [tab, search]);

  useEffect(() => {
    // Typing in the search box waits a moment so each keystroke is not a request.
    const delay = tab === 'manage' ? 400 : 0;
    const wait = setTimeout(() => fetchShops(), delay);

    // New registrations arrive while the page is open: refresh the lists quietly every minute.
    const timer = tab === 'manage' ? null : setInterval(() => fetchShops(true), 60000);

    return () => {
      clearTimeout(wait);
      if (timer) clearInterval(timer);
    };
  }, [fetchShops, tab]);

  const settingsOf = (shop) => settings[shop.id] || initialSettings(shop, managing);

  const change = (shop, patch) =>
    setSettings((current) => ({ ...current, [shop.id]: { ...settingsOf(shop), ...patch } }));

  // Whether a subscription has to be entered: always for an online shop, unless it already has a running one
  // and the person has not asked to add another.
  const needsSubscription = (shop, s) =>
    s.mode === 'online' && (!managing || !hasRunningSubscription(shop) || s.newSubscription);

  // What to tell the person clicking, before the server has to. The server checks it all again.
  const problemWith = (shop, s) => {
    const devices = Number(s.maxDevices);
    if (!Number.isInteger(devices) || devices < 1 || devices > MAX_DEVICES) {
      return `Le nombre de postes doit être entre 1 et ${MAX_DEVICES}`;
    }
    if (needsSubscription(shop, s)) {
      if (!(Number(s.montant) > 0)) return "Indiquez le montant de l'abonnement (FCFA)";
      if (!s.startDate || !s.endDate) return "Indiquez les dates de l'abonnement";
      if (new Date(s.endDate) <= new Date(s.startDate)) return 'La date de fin doit être après la date de début';
    }
    return null;
  };

  const bodyFor = (shop, s) => {
    const body = { mode: s.mode, maxDevices: Number(s.maxDevices) };
    if (needsSubscription(shop, s)) {
      body.subscription = { montant: Number(s.montant), startDate: s.startDate, endDate: s.endDate };
    }
    return body;
  };

  const describe = (s) => `${s.mode === 'online' ? 'en ligne' : 'local'}, ${Number(s.maxDevices)} postes`;

  const approve = async (shop) => {
    const s = settingsOf(shop);
    const problem = problemWith(shop, s);
    if (problem) {
      notify('error', problem);
      return;
    }

    setProcessing(shop.id);
    try {
      const response = await api.put(`/dashboard-admin/registrations/${shop.id}/approve`, bodyFor(shop, s));
      notify(
        'success',
        response.data?.emailed
          ? `« ${shop.nom} » approuvée (${describe(s)}) — e-mail envoyé au propriétaire`
          : `« ${shop.nom} » approuvée (${describe(s)}) — l'e-mail n'a pas pu être envoyé, prévenez le propriétaire`
      );
      setShops((current) => current.filter((x) => x.id !== shop.id));
    } catch (error) {
      notify('error', error.response?.data?.error || "Erreur lors de l'approbation");
    } finally {
      setProcessing(null);
    }
  };

  // "Gérer": change an approved shop. It picks the change up at its next licence refresh.
  const save = async (shop) => {
    const s = settingsOf(shop);
    const problem = problemWith(shop, s);
    if (problem) {
      notify('error', problem);
      return;
    }

    setProcessing(shop.id);
    try {
      const body = bodyFor(shop, s);
      await api.put(`/dashboard-admin/registrations/${shop.id}/settings`, body);
      notify('success', `« ${shop.nom} » : ${describe(s)} — pris en compte à la prochaine connexion de la boutique`);

      // Show what is saved now, without asking the server again.
      setShops((current) =>
        current.map((x) =>
          x.id !== shop.id
            ? x
            : {
                ...x,
                mode: s.mode,
                max_devices: Number(s.maxDevices),
                subscription_end: body.subscription ? body.subscription.endDate : x.subscription_end
              }
        )
      );
      setSettings((current) => ({ ...current, [shop.id]: { ...s, newSubscription: false, montant: '' } }));
    } catch (error) {
      notify('error', error.response?.data?.error || "Erreur lors de l'enregistrement");
    } finally {
      setProcessing(null);
    }
  };

  const reject = async (shop) => {
    if (!window.confirm(`Refuser l'inscription de « ${shop.nom} » ?\nLe propriétaire verra que sa demande n'a pas été acceptée.`)) return;

    setProcessing(shop.id);
    try {
      await api.put(`/dashboard-admin/registrations/${shop.id}/reject`);
      notify('success', `« ${shop.nom} » refusée`);
      setShops((current) => current.filter((x) => x.id !== shop.id));
    } catch (error) {
      notify('error', error.response?.data?.error || 'Erreur lors du refus');
    } finally {
      setProcessing(null);
    }
  };

  const ownerName = (shop) =>
    [shop.firstname, shop.lastname].filter(Boolean).join(' ') || shop.email;

  const emptyMessage = () => {
    if (tab === 'pending') return 'Aucune inscription en attente';
    if (tab === 'rejected') return 'Aucune inscription refusée';
    return search.trim().length < 2
      ? 'Tapez au moins 2 lettres : nom de la boutique, du propriétaire ou e-mail'
      : 'Aucune boutique approuvée ne correspond';
  };

  return (
    <div className="registrations-page">
      {notification && (
        <div className={`reg-notification ${notification.type}`}>
          <FontAwesomeIcon icon={notification.type === 'success' ? faCheck : faTimes} />
          {notification.message}
        </div>
      )}

      <div className="reg-header">
        <h1>
          <FontAwesomeIcon icon={faUserCheck} /> Inscriptions
        </h1>
        <p>Boutiques de l'application Lonnii Desktop : approuver les nouvelles inscriptions, ou régler une boutique déjà approuvée.</p>
      </div>

      <div className="reg-tabs">
        <button className={tab === 'pending' ? 'active' : ''} onClick={() => setTab('pending')}>
          En attente
        </button>
        <button className={tab === 'rejected' ? 'active' : ''} onClick={() => setTab('rejected')}>
          Refusées
        </button>
        <button className={tab === 'manage' ? 'active' : ''} onClick={() => setTab('manage')}>
          Gérer
        </button>
      </div>

      {managing && (
        <div className="reg-search">
          <FontAwesomeIcon icon={faSearch} />
          <input
            type="search"
            placeholder="Rechercher une boutique approuvée (nom, propriétaire, e-mail)…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
          />
        </div>
      )}

      {loading ? (
        <div className="reg-loading">
          <div className="spinner"></div>
          <p>Chargement...</p>
        </div>
      ) : loadError ? (
        <div className="reg-error">
          <p>{loadError}</p>
          <button onClick={() => fetchShops()}>Réessayer</button>
        </div>
      ) : shops.length === 0 ? (
        <div className="reg-empty">
          <FontAwesomeIcon icon={managing ? faSearch : faUserCheck} />
          <p>{emptyMessage()}</p>
        </div>
      ) : (
        <div className="reg-list">
          {shops.map((shop) => {
            const s = settingsOf(shop);
            const busy = processing === shop.id;
            const running = managing && hasRunningSubscription(shop);

            return (
              <div key={shop.id} className="reg-card">
                <div className="reg-card-top">
                  <div className="reg-icon">
                    <FontAwesomeIcon icon={faStore} />
                  </div>

                  <div className="reg-content">
                    <h3>
                      {shop.nom}
                      {!managing && isPhoneShop(shop) && (
                        <span className="reg-badge online">Téléphone · sans ordinateur</span>
                      )}
                      {managing && (
                        <span className={`reg-badge ${shop.mode === 'online' ? 'online' : 'local'}`}>
                          {shop.mode === 'online' ? 'En ligne' : 'Local'}
                        </span>
                      )}
                    </h3>
                    <p className="reg-owner">{ownerName(shop)}</p>
                    <div className="reg-meta">
                      <span><FontAwesomeIcon icon={faEnvelope} /> {shop.email}</span>
                      {shop.phone && <span><FontAwesomeIcon icon={faPhone} /> {shop.phone}</span>}
                      <span>
                        <FontAwesomeIcon icon={faClock} />{' '}
                        {new Date(shop.created_at).toLocaleString('fr-FR')}
                      </span>
                      <span>
                        <FontAwesomeIcon icon={faDesktop} /> {shop.devices_used}/{shop.max_devices} poste(s)
                      </span>
                      {managing && (
                        <span>
                          {running
                            ? `Abonnement actif jusqu'au ${new Date(shop.subscription_end).toLocaleDateString('fr-FR')}`
                            : 'Aucun abonnement actif'}
                        </span>
                      )}
                    </div>
                  </div>

                  {canManage && (
                    <div className="reg-actions">
                      {managing ? (
                        <button className="btn-save" disabled={busy} onClick={() => save(shop)}>
                          <FontAwesomeIcon icon={faSave} />
                          Enregistrer
                        </button>
                      ) : (
                        <>
                          <button className="btn-approve" disabled={busy} onClick={() => approve(shop)}>
                            <FontAwesomeIcon icon={tab === 'rejected' ? faUndo : faCheck} />
                            {tab === 'rejected' ? 'Approuver quand même' : 'Approuver'}
                          </button>
                          {tab === 'pending' && (
                            <button className="btn-reject" disabled={busy} onClick={() => reject(shop)}>
                              <FontAwesomeIcon icon={faTimes} />
                              Refuser
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>

                {canManage && (
                  <div className="reg-options">
                    <div className="reg-field">
                      <label>Mode</label>
                      <div className="reg-segmented" role="group" aria-label="Mode">
                        <button
                          type="button"
                          className={s.mode === 'local' ? 'on' : ''}
                          disabled={!managing && isPhoneShop(shop)}
                          title={!managing && isPhoneShop(shop) ? 'Sans ordinateur : en ligne uniquement' : undefined}
                          onClick={() => change(shop, { mode: 'local' })}
                        >
                          Local
                        </button>
                        <button
                          type="button"
                          className={s.mode === 'online' ? 'on' : ''}
                          onClick={() => change(shop, { mode: 'online' })}
                        >
                          En ligne
                        </button>
                      </div>
                    </div>

                    <div className="reg-field">
                      <label>Postes</label>
                      <input
                        type="number"
                        min="1"
                        max={MAX_DEVICES}
                        value={s.maxDevices}
                        onChange={(e) => change(shop, { maxDevices: e.target.value })}
                      />
                    </div>

                    {s.mode === 'online' && running && (
                      <label className="reg-check">
                        <input
                          type="checkbox"
                          checked={s.newSubscription}
                          onChange={(e) => change(shop, { newSubscription: e.target.checked })}
                        />
                        Ajouter un nouvel abonnement (renouvellement)
                      </label>
                    )}

                    {needsSubscription(shop, s) && (
                      <>
                        <div className="reg-field">
                          <label>Montant (FCFA)</label>
                          <input
                            type="number"
                            min="0"
                            step="1000"
                            placeholder="ex. 120000"
                            value={s.montant}
                            onChange={(e) => change(shop, { montant: e.target.value })}
                          />
                        </div>
                        <div className="reg-field">
                          <label>Début</label>
                          <input
                            type="date"
                            value={s.startDate}
                            onChange={(e) => change(shop, { startDate: e.target.value })}
                          />
                        </div>
                        <div className="reg-field">
                          <label>Fin</label>
                          <input
                            type="date"
                            value={s.endDate}
                            onChange={(e) => change(shop, { endDate: e.target.value })}
                          />
                        </div>
                      </>
                    )}

                    <p className="reg-hint">
                      {s.mode === 'local'
                        ? 'Local : gratuit, fonctionne sans Internet. Aucun abonnement.'
                        : 'En ligne : abonnement requis, sauvegarde en ligne, connexion au serveur au moins tous les 14 jours.'}
                      {managing && ' La boutique prend le changement à sa prochaine connexion au serveur.'}
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Registrations;
